/* eslint-disable no-unused-vars */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { DatePicker } from '@material-ui/pickers';
import { cloneDeep, isEqual, pickBy } from 'lodash';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  TextField,
  makeStyles,
  Typography,
  debounce,
  Theme,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@material-ui/core';
import { listingPriceTypeOptions, listingStatusOptions } from 'src/constants';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import InputAdornment from '@material-ui/core/InputAdornment';
import { LoaderBar, Page } from 'src/components';
import SaveIcon from '@material-ui/icons/Save';
import { useNavigate, useParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  Branch,
  CreateListingProduct,
  CustomInputEvent,
  FixMeLater,
  Listing,
  ProductListingHistoryData,
  warranties
} from 'src/types';
import { Supplier } from 'src/types/supplier';
import { useDebouncedEffect, usePermissions, useSnackBar } from 'src/hooks';
import { cleanSN, getWarrantyInDays, toUtcEquivalentDate } from 'src/utils';
import { ListingPriceTypeEnum, ListingStatusEnum } from 'src/enums';
import FormAddDialog from 'src/components/dialogs/FormDialog';
import { multiBranchFeat } from 'src/constants/feature-toggle';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import useUserInfo from 'src/hooks/user/use-user-info';
import { AuditHistory } from 'src/components/audit/AuditHistory';
import { formatCurrency, formatDate } from 'src/utils';
import { getDynamicsAuditsThunk } from 'src/redux/slices/product/thunks';
import { useLocation } from 'react-router-dom';
import { AddItemTransactionModal } from 'src/views/transactions/components';
import {
  ListingBundleComponents,
  ListingBundleData
} from 'src/redux/slices/listing-bundle/types';
import useResolution from 'src/hooks/useResolution';
import { ListingAddListError } from '../component/ListingAddListError';
import { TemporaryListings } from '../component';
import AddIcon from '@material-ui/icons/Add';
import { generateBundleSerial } from 'src/redux/slices/listing-bundle/util';
import { ConfirmationModal } from 'src/components/modal/ConfirmationModal';
import ReasonDialog from 'src/components/product/ReasonDialog';
import {
  appendInternalRemarks,
  formatInternalRemarks
} from 'src/utils/remarks/remarks';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingBottom: 20
  },
  subHeader: {
    marginBottom: 20
  },
  formControl: {
    marginBottom: theme.spacing(2)
  },
  customerInfoCard: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4)
  },
  marginLeft: {
    marginLeft: theme.spacing(0.5)
  }
}));

const {
  actions: supplierActions,
  selectors: supplierSelectors
} = slices.supplier;
const { actions: listingActions } = slices.listing;
const {
  actions: listingBundleActions,
  selectors: listingBundleSelectors
} = slices.listingBundle;

const ProductListingView = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const snackBar = useSnackBar();
  const location = useLocation();
  const remarksRef = useRef<HTMLSpanElement | null>(null);
  const { xs } = useResolution();
  const {
    canUpdateListing,
    canSeeDealersPrice,
    userPermissionsStringArray
  } = usePermissions();
  const {
    branches,
    getBranches,
    branchInputValue,
    setBranchInputValue,
    getBranchDetails
  } = useBranchInfo();
  const { userBranchOptions } = useUserInfo();

  const newBranchesOptions = useMemo(() => {
    return branches.filter((branch) =>
      userBranchOptions.some((userBranch) => userBranch.value === branch.id)
    );
  }, [branches, userBranchOptions]);

  const { id: listingId } = useParams();

  const suppliers = useAppSelector(supplierSelectors.selectSupplierList);

  const isListingBundleLoading = useAppSelector(
    listingBundleSelectors.selectListingBundleLoading
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listingDetails, setListingDetails] = useState<Listing | undefined>();
  const [warrantyInput, setWarrantyInput] = useState('');
  const [supplierInput, setSupplierInput] = useState('');
  const [isReasonDialogVisible, setIsReasonDialogVisible] = useState(false);
  const [isEditingRemarksOnly, setIsEditingRemarksOnly] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const [isCreateSupplierVisible, setIsCreateSupplierVisible] = useState<
    boolean
  >(false);
  const [addSupplierLoading, setAddSupplierLoading] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<
    boolean
  >(false);
  const [errFields, setErrFields] = useState({
    dr_no: '',
    purchaseDate: '',
    supplier: '',
    warranty: '',
    serial_no: '',
    retail_price: ''
  });
  const [listingAuditData, setListingAuditData] = useState<
    ProductListingHistoryData[]
  >();
  const [isLoadingListingHistory, setIsLoadingHistory] = useState<boolean>(
    false
  );
  const [openAddBundleItemModal, setOpenAddBundleItemModal] = useState<boolean>(
    false
  );
  const [listingBundleSerial, setListingBundleSerial] = useState<
    ListingBundleComponents[]
  >([]);
  const [errRecentListings, setErrRecentListings] = useState<
    CreateListingProduct[]
  >([]);
  const existingRetailPriceRef = useRef<number | undefined>(
    listingDetails?.retail_price
  );
  const originalListingRef = useRef<Listing | null>(null);
  const originalListingBundleRef = useRef<ListingBundleComponents[] | null>(
    null
  );

  const formattedRemarks = listingDetails?.internal_remarks
    ? listingDetails.internal_remarks.split('|').join('\n')
    : '--';

  const menuStatusItemWithPermissions = useMemo(() => {
    const clonedItems = cloneDeep(listingStatusOptions || []);

    return clonedItems.map((item) => {
      const hasPermission =
        !item?.permissions?.length ||
        item.permissions.some((r) => userPermissionsStringArray?.includes(r));

      return {
        ...item,
        disabled: !hasPermission
      };
    });
  }, [userPermissionsStringArray]);

  //creates a reference to check if there is an update
  useEffect(() => {
    if (listingDetails) {
      originalListingRef.current = cloneDeep(listingDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingDetails?.id]);

  useEffect(() => {
    if (listingBundleSerial.length > 0 && !originalListingBundleRef.current) {
      originalListingBundleRef.current = cloneDeep(listingBundleSerial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingBundleSerial]);

  useEffect(() => {
    if (listingDetails) {
      existingRetailPriceRef.current = listingDetails.retail_price;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingDetails?.id]);

  const fieldValidations = [
    {
      field: 'dr_no',
      value: listingDetails?.dr_no,
      errorMessage: 'DR NO IS REQUIRED'
    },
    {
      field: 'purchaseDate',
      value: listingDetails?.purchase_date,
      errorMessage: 'PURCHASE DATE ON SUPPLIER IS REQUIRED'
    },
    {
      field: 'supplier',
      value: listingDetails?.supplier,
      errorMessage: 'SUPPLIER IS REQUIRED'
    },
    {
      field: 'warranty',
      value: warrantyInput,
      errorMessage: 'WARRANTY IS REQUIRED'
    },
    {
      field: 'serial_no',
      value: listingDetails?.serial_no,
      errorMessage: 'SERIAL NUMBER IS REQUIRED! '
    },
    ...(listingDetails?.product?.is_product_bundle
      ? [
          {
            field: 'retail_price',
            value: listingDetails?.retail_price,
            errorMessage: 'RETAIL PRICE IS REQUIRED FOR BUNDLES',
            required: true
          }
        ]
      : [])
  ];

  const formatListingBundleForDisplay = listingBundleSerial.map((item) => ({
    serial_no: item?.component_listing?.serial_no,
    product_name: item?.component_listing?.product?.name,
    retail_price: item?.component_listing?.product?.retail_price
  }));

  const totalRetailPrice = formatListingBundleForDisplay.reduce(
    (sum, item) => sum + (item?.retail_price || 0),
    0
  );

  //checks form fields for changes
  const getChangedFields = (original: any, current: any) => {
    return pickBy(current, (value, key) => {
      return !isEqual(value, original[key]);
    });
  };

  // checks component changes
  const haveBundleComponentsChanged = (
    original: ListingBundleComponents[] | null,
    current: ListingBundleComponents[]
  ) => {
    if (!original) return current.length > 0;

    return current.some((item, index) => {
      const originalItem = original[index];
      if (!originalItem) return true;
      return (
        item?.listing_id !== originalItem?.listing_id ||
        item?.component_listing?.product_id !==
          originalItem.component_listing?.product_id
      );
    });
  };

  // eslint-disable-next-line no-undef
  const onChangeBranch = async (branch: Partial<Branch> | null) => {
    const branchDetails = branch
      ? (await getBranchDetails(branch)).originalData?.data
      : null;
    if (branchDetails) {
      setListingDetails((prev) => {
        if (prev?.id) {
          return {
            ...prev,
            branch: branchDetails ?? undefined,
            branch_id: branchDetails?.id
          };
        }
      });
    }
  };

  const onOpenCreateSupplierDialog = () => {
    setIsCreateSupplierVisible(true);
  };

  const onCloseCreateSupplierDialog = () => {
    setIsCreateSupplierVisible(false);
    setAddSupplierLoading(false);
  };

  const getProductListingHistoryUpdates = useCallback(
    async (listingId?: string) => {
      try {
        setIsLoadingHistory(true);
        const getAuditListingData = unwrapResult(
          await dispatch(
            getDynamicsAuditsThunk({
              auditable_id: listingId || '0',
              auditable_type: 'Listings'
            })
          )
        );
        if (getAuditListingData?.success) {
          setIsLoadingHistory(false);
          const { data } = getAuditListingData?.originalData;
          setListingAuditData(data || []);
        }
      } catch (error) {
        setIsLoadingHistory(false);
        console.error(error);
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [dispatch]
  );

  const onAddSupplierOnModalPress = async (name: string) => {
    // TODO: Add checker loader before close.
    setAddSupplierLoading(true);
    if (name) {
      const response = unwrapResult(
        await dispatch(
          supplierActions.createSupplierThunk({
            company_name: name,
            company_details: [{ name }]
          })
        )
      );
      if (response.success) {
        snackBar.show({ severity: 'success', message: response.message });
        onCloseCreateSupplierDialog();
      } else {
        snackBar.show({ severity: 'error', message: response.message });
        setAddSupplierLoading(false);
      }
    } else {
      snackBar.show({
        severity: 'error',
        message: 'Please fill up all required information.'
      });
    }
  };

  const areFieldsValid = () => {
    let areValid = true;

    fieldValidations.forEach((validation) => {
      const { field, value, errorMessage } = validation;
      if (
        field === 'retail_price'
          ? value === '' || value === null || value === undefined
          : !value
      ) {
        setErrFields((prev) => ({ ...prev, [field]: errorMessage }));
        areValid = false;
      } else {
        setErrFields((prev) => ({ ...prev, [field]: '' }));
      }
    });

    return areValid;
  };

  const validateField = (name: string, value: any) => {
    const targetValidation = fieldValidations.find(
      (validation) => validation.field === name
    );

    if (targetValidation) {
      const { field, errorMessage } = targetValidation;

      if (
        !value ||
        (field === 'retail_price' &&
          (value === '' || value === null || value === undefined))
      ) {
        setErrFields((prev) => ({ ...prev, [field]: errorMessage }));
      } else {
        setErrFields((prev) => ({ ...prev, [field]: '' }));
      }
    }
  };

  const warrantyValue = useMemo(() => {
    const x = warranties?.find(
      (x) => x.value === listingDetails?.warranty_duration
    );
    return x ?? null;
  }, [listingDetails]);

  const isDealersPriceFieldDisabled = useMemo(
    () =>
      listingDetails?.price_type === ListingPriceTypeEnum.FreeFromSupplier ||
      listingDetails?.price_type === ListingPriceTypeEnum.FreeForAll,
    [listingDetails]
  );

  const isRetailPriceFieldDisabled = useMemo(
    () => listingDetails?.price_type === ListingPriceTypeEnum.FreeForAll,
    [listingDetails]
  );

  const getSuppliers = debounce(async (keyword: string = '') => {
    dispatch(supplierActions.getSuppliersThunk({ keyword, limit: 9999 }));
  }, 500);

  const handleChangeFreebies = (event: FixMeLater) => {
    if (listingDetails?.id) {
      if (event.target.value === ListingPriceTypeEnum.NotFree) {
        setListingDetails((prev) => ({
          ...prev,
          id: listingDetails?.id,
          dealers_price: 0,
          price_type: event.target.value
        }));
      }
      if (event.target.value === ListingPriceTypeEnum.FreeFromSupplier) {
        setListingDetails((prev) => ({
          ...prev,
          id: listingDetails?.id,
          dealers_price: 0,
          price_type: event.target.value
        }));
      }
      if (event.target.value === ListingPriceTypeEnum.FreeForAll) {
        setListingDetails((prev) => ({
          ...prev,
          id: listingDetails?.id,
          dealers_price: 0,
          retail_price: 0,
          price_type: event.target.value
        }));
      }
    }
  };

  const onConfirmRecompute = (recompute: boolean) => {
    listingDetails?.product?.is_product_bundle
      ? onUpdateListingBundle()
      : updateItem(recompute);
  };

  const handleChangeStatus = (event: FixMeLater) => {
    if (listingDetails?.id) {
      setListingDetails((prev) => ({
        ...prev,
        id: listingDetails?.id,
        status: event.target.value
      }));
    }
  };

  const updateItem = async (
    recompute: boolean = false,
    overrideListingDetails?: Listing
  ) => {
    const currentListingDetails = overrideListingDetails || listingDetails;

    // This should be the only ones editable.
    if (!canUpdateListing) {
      return;
    }

    if (!areFieldsValid()) {
      snackBar.show({
        severity: 'error',
        message: 'Please complete required fields',
        useSound: true
      });
      return;
    }

    if (currentListingDetails) {
      let sn = currentListingDetails.serial_no;
      setIsLoading(true);
      const data: Listing = {
        ...currentListingDetails,
        serial_no: sn ? cleanSN(sn) : '',
        recompute_bundle_price: recompute
      };

      if (
        existingRetailPriceRef.current === currentListingDetails.retail_price
      ) {
        delete data.retail_price;
      }

      const response = unwrapResult(
        await dispatch(listingActions.updateListingsThunk(data)).finally(() =>
          setIsLoading(false)
        )
      );
      if (response?.success && response?.originalData?.listing) {
        getListingDetails().finally(() => {
          snackBar.show({ severity: 'success', message: response?.message });
        });
        return;
      }

      // if not success and has error message
      if (!response?.success && response?.errors) {
        snackBar.show({
          severity: 'error',
          message: `${JSON.stringify(response?.errors)}`
        });
        return;
      }

      snackBar.show({ severity: 'error', message: 'Listing not updated.' });
    }
  };

  const getListingDetails = useCallback(async () => {
    if (listingId) {
      setIsLoading(true);
      const response = unwrapResult(
        await dispatch(
          listingActions.getListingsViaIdThunk(+listingId)
        ).finally(() => setIsLoading(false))
      );
      if (response?.success && response.originalData.listing) {
        setListingDetails(response.originalData.listing);
        if (response.originalData?.listing?.components?.[0]) {
          setListingBundleSerial(
            response?.originalData?.listing?.components.map((component) => ({
              main_listing_id: component.main_listing_id,
              listing_id: component.listing_id,
              component_listing: component.component_listing
            }))
          );
        }
        getProductListingHistoryUpdates(listingId);
      }
    }
  }, [dispatch, getProductListingHistoryUpdates, listingId]);

  const handleChange = (event: CustomInputEvent) => {
    const { name, value } = event.target;
    let modifiedValue = value;
    if (name === 'dr_no') {
      modifiedValue = modifiedValue.toUpperCase();
    }
    setListingDetails((prev) => {
      if (prev?.id) {
        return {
          ...prev,
          [name]: modifiedValue
        };
      }
    });
  };

  const setPurchaseDate = (date: Date | null) => {
    setListingDetails((prev) => {
      if (prev?.id) {
        return {
          ...prev,
          purchase_date: date
            ? toUtcEquivalentDate(date)?.toISOString()
            : undefined
        };
      }
    });
  };

  const onUpdateSupplier = (supplier: Supplier | null) => {
    setListingDetails((prev) => {
      if (prev?.id) {
        return {
          ...prev,
          supplier: supplier ?? undefined,
          supplier_id: supplier?.id
        };
      }
    });
  };

  const onUpdateWarranty = (warranty: FixMeLater | null) => {
    setListingDetails((prev) => {
      if (prev?.id) {
        return {
          ...prev,
          warranty_duration: warranty?.value ?? undefined
        };
      }
    });
  };

  const getPriceType = (priceTypeId?: number) => {
    const priceTypeName = listingPriceTypeOptions.find(
      (price) => price.value === priceTypeId
    );
    return priceTypeName?.name || '--';
  };

  const onUpdateListingBundle = async () => {
    if (!listingDetails || !originalListingRef.current) return;

    if (errRecentListings.length > 0) {
      alert('Please clear all Invalid Serial Nos before proceeding');
      return;
    }
    if (
      listingDetails?.product?.is_product_bundle &&
      listingDetails.retail_price?.toString() === ''
    ) {
      setErrFields((prev) => ({
        ...prev,
        retail_price: 'RETAIL PRICE IS REQUIRED FOR BUNDLES'
      }));

      snackBar.show({
        severity: 'error',
        message: 'Retail price is required for bundle products'
      });

      return;
    }

    try {
      setIsLoading(isListingBundleLoading);

      const changes = getChangedFields(
        originalListingRef.current,
        listingDetails
      );

      const componentsChanged = haveBundleComponentsChanged(
        originalListingBundleRef.current,
        listingBundleSerial
      );

      if (Object.keys(changes).length === 0 && !componentsChanged) {
        snackBar.show({
          severity: 'info',
          message: 'No changes detected'
        });
        return;
      }

      const cleanedListingBundle = listingBundleSerial.map((item) => ({
        listing_id: Number(item.listing_id ?? 0),
        product_id: Number(item.component_listing?.product_id ?? 0)
      }));

      const newListingBundleData = {
        id: listingDetails.id,
        ...changes
      };

      const payload: any = {
        id: listingDetails?.id,
        components: cleanedListingBundle,
        listing_bundle_data: newListingBundleData
      };
      //for future reference, if components are not in payload if no changes made
      // if(componentsChanged){
      //   payload.components= cleanedListingBundle;
      // }

      const response = unwrapResult(
        await dispatch(listingBundleActions.updateListingBundleThunk(payload))
      );
      const originalData = response?.originalData ?? {};
      if (originalData?.success) {
        getListingDetails().finally(() => {
          snackBar.show({ severity: 'success', message: originalData.message });
        });
        return;
      } else {
        const errors = originalData?.errors;
        const message = originalData?.message;
        snackBar.show({
          severity: 'error',
          message: errors ? Object.values(errors).join(', ') : message
        });
      }
    } catch (error) {
      console.error('Something went wrong:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addBundleComponents = (
    prevComponents: ListingBundleComponents[],
    newListing: Listing,
    snackBar: any
  ): ListingBundleComponents[] => {
    const exists = prevComponents.some(
      (item) => item.listing_id === newListing.id.toString()
    );

    if (exists) {
      snackBar.show({
        severity: 'error',
        message: 'Serial number already exists in this bundle'
      });
      return prevComponents;
    }

    const newComponent: ListingBundleComponents = {
      main_listing_id: newListing.id,
      listing_id: newListing.id.toString(),
      component_listing: {
        serial_no: newListing.serial_no,
        product_id: newListing.product_id || 0,
        product: {
          name: newListing.product_name,
          retail_price: newListing.retail_price || 0
        }
      }
    };

    return [...prevComponents, newComponent];
  };

  const onPressDeleteRecentItem = (index: number) => {
    setListingBundleSerial((prev) => {
      const cloned = [...prev];
      cloned.splice(index, 1);
      return cloned;
    });
  };

  const handleSaveListing = () => {
    if (listingDetails?.status === ListingStatusEnum.OnHold) {
      setIsReasonDialogVisible(true);
      return;
    }

    listingDetails?.product?.is_product_bundle
      ? onUpdateListingBundle()
      : existingRetailPriceRef.current !== listingDetails?.retail_price &&
        listingDetails?.status === 'bundled'
      ? setIsConfirmationModalOpen(true)
      : updateItem();
  };

  const handleSaveReason = (reason: string) => {
    if (!listingDetails) {
      return;
    }

    const newRemark = formatInternalRemarks({
      remarks: reason
    });

    const updatedInternalRemarks = appendInternalRemarks(
      listingDetails.internal_remarks,
      newRemark
    );

    updateItem(false, {
      ...listingDetails,
      internal_remarks: updatedInternalRemarks
    });

    setIsReasonDialogVisible(false);
  };

  const handleCloseReasonDialog = () => {
    setIsEditingRemarksOnly(false);
    setIsReasonDialogVisible(false);
  };

  const handleEditRemarks = () => {
    setIsEditingRemarksOnly(true);
    setIsReasonDialogVisible(true);
  };

  useEffect(() => {
    getListingDetails();
  }, [getListingDetails]);

  useDebouncedEffect(() => getSuppliers(supplierInput), 500, [supplierInput]);
  useDebouncedEffect(() => getBranches(), 500, [branchInputValue]);

  useEffect(() => {
    const el = remarksRef.current;

    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingDetails?.internal_remarks]);

  // useEffect(() => {
  //   if (listingDetails?.components) {
  //     setListingBundleSerial(
  //       listingDetails.components.map((component) => ({
  //         main_listing_id: component.main_listing_id,
  //         listing_id: component.listing_id,
  //         component_listing: component.component_listing
  //       }))
  //     );
  //   }
  // }, [listingDetails]);

  return (
    <Page
      className={classes.root}
      title={`Listing Edit ${listingDetails?.serial_no || ''}`}
    >
      <LoaderBar isLoading={isLoading} />
      <Container maxWidth={false}>
        <Box mt={2}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            mb={2}
          >
            <Typography
              color="textPrimary"
              variant="h3"
            >{`Listing Edit ${listingDetails?.serial_no || ''}`}</Typography>
          </Box>

          <Card
            hidden={!listingDetails?.purchase_info}
            className={classes.customerInfoCard}
          >
            <CardContent>
              <Typography className={classes.subHeader} variant="h4">
                Customer Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <Typography display="inline" variant="h5">
                    Transaction No:
                    <Link
                      className={classes.marginLeft}
                      component="button"
                      variant="body1"
                      onClick={() =>
                        navigate(
                          `/app/transaction/${listingDetails?.purchase_info?.transaction_no}`
                        )
                      }
                      display="inline"
                    >
                      {` ${listingDetails?.purchase_info?.transaction_no ??
                        'Not Available'}`}
                    </Link>
                  </Typography>
                </Grid>
                <Grid item md={6} xs={12}>
                  <Typography display="inline" variant="h5">
                    Customer Full Name:
                    <Link
                      className={classes.marginLeft}
                      component="button"
                      variant="body1"
                      onClick={() =>
                        navigate(
                          `/app/customers/${
                            listingDetails?.purchase_info?.customer_id
                          }?is_ecomm_user=${
                            listingDetails?.purchase_info?.is_ecomm_user ? 1 : 0
                          }`
                        )
                      }
                      display="inline"
                    >
                      {` ${listingDetails?.purchase_info?.full_name ??
                        'Not Available'}`}
                    </Link>
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography className={classes.subHeader} variant="h6">
                General Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    label="Part no. (optional)"
                    name="part_no"
                    onChange={(e) => {
                      handleChange(e);
                      validateField(e?.target?.name, e?.target?.value);
                    }}
                    value={listingDetails?.part_no ?? ''}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    type="sku"
                    label="SKU"
                    name="sku"
                    onChange={handleChange}
                    value={listingDetails?.sku ?? ''}
                    variant="outlined"
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    error={errFields?.serial_no ? true : false}
                    required
                    fullWidth
                    name="serial_no"
                    label="Serial no."
                    variant="outlined"
                    onChange={(e) => {
                      handleChange(e);
                      validateField(e?.target?.name, e?.target?.value);
                    }}
                    value={listingDetails?.serial_no ?? ''}
                    disabled={
                      listingDetails?.purchase_info
                        ? true
                        : false || listingDetails?.product?.is_product_bundle
                    }
                    helperText={
                      <>
                        {errFields?.serial_no}
                        <br />
                        Warning: Editing this while on a transaction might
                        result to inconsistencies
                      </>
                    }
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    className={classes.formControl}
                  >
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={listingDetails?.status ?? ''}
                      onChange={handleChangeStatus}
                      label="Status"
                    >
                      {menuStatusItemWithPermissions?.map((item) => (
                        <MenuItem
                          key={item?.value}
                          value={item?.value}
                          disabled={item?.disabled}
                        >
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {listingDetails?.internal_remarks && (
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      mt={-1}
                    >
                      <Tooltip
                        title={
                          <span style={{ whiteSpace: 'pre-line' }}>
                            {formattedRemarks}
                          </span>
                        }
                        disableHoverListener={!isTruncated}
                        arrow
                      >
                        <Typography
                          ref={remarksRef}
                          variant="caption"
                          color="textSecondary"
                          style={{
                            whiteSpace: 'pre-line',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '85%',
                            cursor: isTruncated ? 'pointer' : 'default'
                          }}
                        >
                          Internal Remarks: {''}
                          {formattedRemarks}
                        </Typography>
                      </Tooltip>

                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={handleEditRemarks}
                        disabled={!canUpdateListing}
                      >
                        Edit
                      </Button>
                    </Box>
                  )}
                </Grid>

                {listingDetails?.product?.is_product_bundle ? (
                  <>
                    <Grid item md={12}>
                      <Grid
                        container
                        spacing={2}
                        alignItems={xs ? 'stretch' : 'center'}
                      >
                        <Grid item xs={12} sm={6} md={6}>
                          <Autocomplete
                            value={
                              listingDetails?.branch_id
                                ? newBranchesOptions.find(
                                    (b) => b.id === listingDetails.branch_id
                                  ) || null
                                : null
                            }
                            onChange={(_, newValue) => {
                              onChangeBranch(newValue);
                            }}
                            inputValue={branchInputValue}
                            onInputChange={(_, newInputValue) => {
                              setBranchInputValue(newInputValue);
                            }}
                            noOptionsText={`No such Branch.`}
                            id="branch"
                            options={newBranchesOptions}
                            getOptionLabel={(option) => option.branch_name}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required
                                label="Branch"
                                variant="outlined"
                              />
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6} md={6}>
                          <Button
                            color="primary"
                            variant="contained"
                            onClick={() => {
                              if (!listingDetails?.branch_id) {
                                snackBar.show({
                                  severity: 'error',
                                  message: 'select branch first'
                                });
                                return;
                              }
                              setOpenAddBundleItemModal(true);
                            }}
                            style={{ height: 56, width: xs ? '100%' : 'auto' }}
                            startIcon={<AddIcon />}
                            fullWidth={xs}
                          >
                            Add Serial No.
                          </Button>
                        </Grid>
                      </Grid>

                      <ListingAddListError
                        listingErrorList={errRecentListings}
                        onClearAll={() => setErrRecentListings([])}
                        onRemove={(index) =>
                          setErrRecentListings((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                      />

                      <TemporaryListings
                        product={listingDetails}
                        recentListings={[]}
                        bundleListings={formatListingBundleForDisplay}
                        onPressDeleteRecentItem={onPressDeleteRecentItem}
                        showOnlySerialNo={false}
                        addBundle={true}
                        totalRetailPrice={totalRetailPrice}
                        isListingView={true}
                      />
                      {totalRetailPrice !==
                        Number(listingDetails?.retail_price ?? 0) && (
                        <Typography variant="h5" style={{ color: 'red' }}>
                          Warning: Total retail price of components and bundle
                          retail price is not the same.
                        </Typography>
                      )}
                    </Grid>
                  </>
                ) : null}
              </Grid>
            </CardContent>
            <Divider />
            <CardContent>
              <Typography className={classes.subHeader} variant="h6">
                Pricing
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    className={classes.formControl}
                  >
                    <InputLabel>Price Type</InputLabel>
                    <Select
                      value={listingDetails?.price_type ?? ''}
                      onChange={handleChangeFreebies}
                      label="Price Type"
                    >
                      {listingPriceTypeOptions?.map((item) => (
                        <MenuItem key={item?.value} value={item?.value}>
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {canSeeDealersPrice ? (
                  <Grid item md={6} xs={12}>
                    <TextField
                      fullWidth
                      type="number"
                      disabled={isDealersPriceFieldDisabled}
                      label="Dealers Price"
                      name="dealers_price"
                      onChange={handleChange}
                      value={listingDetails?.dealers_price ?? ''}
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₱</InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                ) : null}

                <Grid item md={6} xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    disabled={isRetailPriceFieldDisabled}
                    label="Retail Price"
                    name="retail_price"
                    onChange={(e) => {
                      handleChange(e);
                      if (listingDetails?.product?.is_product_bundle) {
                        validateField('retail_price', e.target.value);
                      }
                    }}
                    value={listingDetails?.retail_price ?? ''}
                    variant="outlined"
                    error={errFields?.retail_price ? true : false}
                    helperText={errFields?.retail_price}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₱</InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
            <Divider />
            <CardContent>
              <Typography className={classes.subHeader} variant="h6">
                Other Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <Box>
                    {/* TODO: https://v4.mui.com/components/autocomplete/#FreeSoloCreateOptionDialog.tsx */}
                    <Autocomplete
                      value={listingDetails?.supplier ?? null}
                      onChange={(_, newValue) => {
                        onUpdateSupplier(newValue);
                        validateField('supplier', newValue);
                      }}
                      onInputChange={(_, newInputValue) => {
                        setSupplierInput(newInputValue);
                      }}
                      noOptionsText={`"${supplierInput}" has not been found in suppliers. Please add it first`}
                      id="supplier"
                      options={suppliers}
                      getOptionSelected={(option, value) =>
                        option.id === value.id
                      }
                      getOptionLabel={(option) => option?.name || ''}
                      renderInput={(params) => (
                        <TextField
                          error={errFields?.supplier ? true : false}
                          helperText={errFields?.supplier}
                          {...params}
                          required
                          label="Supplier"
                          variant="outlined"
                        />
                      )}
                    />
                    <Button
                      style={{ padding: 0, paddingTop: 5, paddingLeft: 5 }}
                      onClick={onOpenCreateSupplierDialog}
                      color="primary"
                      variant="text"
                    >
                      add a supplier
                    </Button>
                  </Box>
                </Grid>
                {multiBranchFeat &&
                !listingDetails?.product?.is_product_bundle ? (
                  <Grid item md={6} xs={12}>
                    <Autocomplete
                      value={
                        listingDetails?.branch_id
                          ? newBranchesOptions.find(
                              (b) => b.id === listingDetails.branch_id
                            ) || null
                          : null
                      }
                      onChange={(_, newValue) => {
                        onChangeBranch(newValue);
                      }}
                      inputValue={branchInputValue}
                      onInputChange={(_, newInputValue) => {
                        setBranchInputValue(newInputValue);
                      }}
                      noOptionsText={`No such Branch.`}
                      id="branch"
                      options={newBranchesOptions}
                      getOptionLabel={(option) => option.branch_name}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          label="Branch"
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>
                ) : null}
                <Grid item md={6} xs={12}>
                  <Autocomplete
                    // freeSolo
                    value={warrantyValue}
                    onChange={(_, newValue) => {
                      onUpdateWarranty(newValue);
                      validateField('warranty', newValue);
                    }}
                    inputValue={warrantyInput}
                    onInputChange={(_, newInputValue) => {
                      setWarrantyInput(newInputValue);
                    }}
                    id="warranty"
                    options={warranties}
                    getOptionSelected={(option, value) =>
                      option.value === value.value
                    }
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        error={errFields?.warranty ? true : false}
                        helperText={errFields?.warranty}
                        {...params}
                        required
                        label="Warranty Duration (days)"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <DatePicker
                    error={errFields?.purchaseDate ? true : false}
                    helperText={errFields?.purchaseDate}
                    fullWidth
                    // maxDate={new Date()}
                    format="MMMM dd, yyyy"
                    inputVariant="outlined"
                    name="purchase_date"
                    label="Purchase Date on Supplier"
                    value={listingDetails?.purchase_date ?? null}
                    onChange={(date) => setPurchaseDate(date)}
                    animateYearScrolling
                  />
                </Grid>
                <Grid item md={6} xs={12}>
                  <TextField
                    error={errFields?.dr_no ? true : false}
                    helperText={errFields?.dr_no}
                    fullWidth
                    required
                    label="DR No."
                    name="dr_no"
                    onChange={(e) => {
                      handleChange(e);
                      validateField(e?.target?.name, e?.target?.value);
                    }}
                    value={listingDetails?.dr_no ?? ''}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </CardContent>
            {canUpdateListing ? (
              <Box display="flex" justifyContent="flex-end" p={2}>
                <Button
                  startIcon={<SaveIcon />}
                  style={{ marginLeft: 18 }}
                  onClick={handleSaveListing}
                  color="primary"
                  variant="contained"
                >
                  Save
                </Button>
              </Box>
            ) : null}
          </Card>
          <FormAddDialog
            loading={addSupplierLoading}
            fieldName="Supplier"
            title="Create Supplier"
            isVisible={isCreateSupplierVisible}
            subTitle="Input Supplier"
            onAddPress={onAddSupplierOnModalPress}
            handleClose={onCloseCreateSupplierDialog}
          />
        </Box>

        <AuditHistory
          summary={`View Update/Edit History For Serial No.${listingDetails?.serial_no}`}
          auditData={listingAuditData || []}
          isLoading={isLoadingListingHistory}
          expandFirstCard={true}
          moduleName="Product Listing"
          formatValue={(key: any, value: any) => {
            if (key === 'price_type') {
              return getPriceType(value);
            }
            if (key === 'dealers_price' || key === 'retail_price') {
              return formatCurrency(value);
            }
            if (key === 'purchase_date') {
              return formatDate(value);
            }
            return value;
          }}
        />
      </Container>

      <AddItemTransactionModal
        onHandleClose={() => setOpenAddBundleItemModal(false)}
        visible={openAddBundleItemModal}
        mode={'add_bundle'}
        onAddBundle={(listing) => {
          setListingBundleSerial((prev) =>
            addBundleComponents(prev, listing, snackBar)
          );
          setOpenAddBundleItemModal(false);
        }}
        selectedBranch={
          listingDetails?.branch_id
            ? newBranchesOptions.find(
                (b) => b.id === listingDetails.branch_id
              ) || undefined
            : undefined
        }
        productName={listingDetails?.product_name || listingDetails?.serial_no}
      />

      {listingDetails?.status === 'bundled' ? (
        <ConfirmationModal
          visible={isConfirmationModalOpen}
          onHandleClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={onConfirmRecompute}
          title="Bundle Pricing Update"
          contentText={
            'This product is part of a bundle. \nChoose how you want to apply the retail price change.'
          }
          item="listing"
        />
      ) : null}

      <ReasonDialog
        title={
          isEditingRemarksOnly ? 'Edit Internal Remarks' : 'Set as On Hold'
        }
        subTitle={
          isEditingRemarksOnly
            ? 'Update the internal remarks for this listing.'
            : 'Please provide the reason for setting this listing as On Hold.'
        }
        label="Internal Remarks"
        placeholder="Enter remarks"
        initialValue={listingDetails?.internal_remarks ?? ''}
        isVisible={isReasonDialogVisible}
        handleClose={handleCloseReasonDialog}
        onSavePress={handleSaveReason}
      />
    </Page>
  );
};

export default ProductListingView;
